import { useVerifyAuthenticatorMfaMutation } from '@codezo/graphql';
import { useThemeColors } from '@codezo/theme';
import {
	Alert,
	Anchor,
	Box,
	Button,
	Group,
	Paper,
	PinInput,
	Stack,
	Text,
	TextInput,
	Title,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/common/Logo';
import { handleLoginValidate } from '../utils/login';

export function MFAVerifyPage() {
	const [code, setCode] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [useBackupCode, setUseBackupCode] = useState(false);
	const [backupCode, setBackupCode] = useState('');
	const navigate = useNavigate();
	const location = useLocation();

	const colors = useThemeColors();

	// Get all state from navigation state
	const userId = location.state?.userId as string | undefined;
	const supportsBackupCodes = location.state?.supportsBackupCodes as boolean | undefined;

	useEffect(() => {
		if (!userId) {
			navigate('/auth/login');
		}
	}, [userId, navigate]);

	// For authenticator MFA validation during login
	const [verifyAuthenticatorMfa, { loading }] = useVerifyAuthenticatorMfaMutation({
		onCompleted: (data) => {
			if (data.verifyAuthenticatorMfa.token && data.verifyAuthenticatorMfa.role) {
				// Use role from the response
				handleLoginValidate(
					data.verifyAuthenticatorMfa.token,
					data.verifyAuthenticatorMfa.role
				);
			}
		},
		onError: (err) => {
			if (useBackupCode) {
				setError(err.message || 'Invalid backup code. Please check and try again.');
			} else {
				setError(err.message || 'Invalid verification code. Please try again.');
			}
		},
	});

	const handleVerify = async () => {
		if (!userId) return;

		// Check the appropriate code based on mode
		const codeToVerify = useBackupCode ? backupCode : code;
		if (useBackupCode && !backupCode) return;
		if (!useBackupCode && code.length !== 6) return;

		setError(null);

		try {
			// Always use verifyAuthenticatorMfa for login
			await verifyAuthenticatorMfa({
				variables: {
					input: {
						userId,
						token: codeToVerify,
						isBackupCode: useBackupCode,
					},
				},
			});
		} catch {
			// Error is already handled by onError callback
			// Error handled by mutation onError
		}
	};

	return (
		<Box style={{ position: 'relative' }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Paper miw={480}>
					<Stack>
						<Stack gap="sm" ta="center">
							<Logo height={40} />
							<div>
								<Title mb={4}>Two-Factor Authentication</Title>
								<Text size="sm" c={colors.textMuted}>
									{useBackupCode
										? 'Enter your backup code'
										: 'Enter the 6-digit code from your authenticator app'}
								</Text>
							</div>
						</Stack>

						{error && (
							<Alert
								icon={<IconAlertCircle size={20} color={colors.error} />}
								color="error"
							>
								{error}
							</Alert>
						)}

						<Stack gap="xl" align="center">
							{!useBackupCode ? (
								<PinInput
									length={6}
									type="number"
									size="xl"
									value={code}
									onChange={setCode}
									disabled={loading}
									onComplete={handleVerify}
									error={!!error}
								/>
							) : (
								<TextInput
									placeholder="XXXX-XXXX"
									label="Backup Code"
									value={backupCode}
									onChange={(e) =>
										setBackupCode(e.currentTarget.value.toUpperCase())
									}
									disabled={loading}
									error={!!error}
									size="lg"
									style={{ width: '100%' }}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && backupCode) {
											handleVerify();
										}
									}}
								/>
							)}

							{loading && (
								<Text size="sm" c={colors.textMuted}>
									Verifying...
								</Text>
							)}
						</Stack>

						<Button
							fullWidth
							onClick={handleVerify}
							loading={loading}
							disabled={useBackupCode ? !backupCode : code.length !== 6}
							size="sm"
						>
							Verify
						</Button>

						{supportsBackupCodes && (
							<Group justify="center">
								<Anchor
									onClick={() => {
										setUseBackupCode(!useBackupCode);
										setCode('');
										setBackupCode('');
										setError(null);
									}}
									component="button"
									size="sm"
								>
									{useBackupCode
										? 'Use authenticator app'
										: 'Lost your device? Use backup code'}
								</Anchor>
							</Group>
						)}

						<Group justify="center">
							<Anchor
								component={Link}
								to="/auth/login"
								style={{ display: 'flex', alignItems: 'center', gap: 4 }}
							>
								<IconArrowLeft size={16} />
								Back to login
							</Anchor>
						</Group>
					</Stack>
				</Paper>
			</motion.div>
		</Box>
	);
}
